import { useState, useEffect } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Live Sky Test - Observatory Verification
 * 
 * Required for Grades 10-12 and Seminary students.
 * Students must verify real-time astronomical data to advance.
 */

function LiveSkyTest({ user, grade }) {
  const [testType, setTestType] = useState(null);
  const [answers, setAnswers] = useState({});
  const [celestialData, setCelestialData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [testStatus, setTestStatus] = useState(null);

  useEffect(() => {
    loadCelestialData();
    loadTestStatus();
  }, []);

  const loadCelestialData = async () => {
    try {
      const token = localStorage.getItem("token");
      
      // GEOGRAPHIC ANCHOR: North American Continent - Mississippi River Valley (St. Louis/Cahokia)
      // Fallback coordinates: ~38.6270° N, 90.1994° W (St. Louis, MO - center of the Promised Land)
      let lat = 38.6270;
      let lon = -90.1994;
      
      // ALWAYS try to get user's device location first
      if (navigator.geolocation) {
        try {
          const pos = await new Promise((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject, { 
              timeout: 10000,
              enableHighAccuracy: true,
              maximumAge: 300000 // Cache for 5 minutes
            });
          });
          lat = pos.coords.latitude;
          lon = pos.coords.longitude;
        } catch (e) {
          console.log("Geolocation denied/unavailable - using St. Louis/Cahokia default");
        }
      }
      
      const response = await axios.get(
        `${API}/celestial/live-observatory?lat=${lat}&lon=${lon}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Extract nested data structure
      const liveObs = response.data?.live_observatory || {};
      const solar = liveObs.solar_ephemeris || {};
      const lunar = liveObs.lunar_ephemeris || {};
      const enochian = response.data?.enochian_calendar || {};
      
      // Normalize to component expected format
      const normalizedData = {
        sun: {
          longitude: solar.ecliptic_longitude || 0,
          altitude: solar.altitude || 30
        },
        moon: {
          longitude: lunar.ecliptic_longitude || 0,
          phase_name: lunar.phase || "Unknown",
          illumination: (lunar.illumination_percent || 0) / 100
        },
        enochian: {
          portal: solar.current_gate || 1,
          day_name: enochian.day_name || "Unknown"
        }
      };
      
      setCelestialData(normalizedData);
    } catch (err) {
      console.error("Error loading celestial data:", err);
    } finally {
      setLoading(false);
    }
  };

  const loadTestStatus = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/progress/live-sky-test/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTestStatus(response.data);
    } catch (err) {
      console.error("Error loading test status:", err);
    }
  };

  const submitTest = async () => {
    setSubmitting(true);
    setResult(null);
    
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API}/progress/live-sky-test/submit`,
        {
          grade: grade || "10",
          test_type: testType,
          answers: answers
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setResult(response.data);
      
      // Reload test status
      loadTestStatus();
      
    } catch (err) {
      console.error("Error submitting test:", err);
      setResult({
        passed: false,
        score: 0,
        feedback: ["Error submitting test. Please try again."]
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Test configurations by type
  const tests = {
    sun_longitude_test: {
      name: "Sun Longitude Verification",
      description: "Verify the current position of the Sun using the Live Observatory.",
      fields: [
        { key: "sun_longitude", label: "Current Sun Ecliptic Longitude (degrees)", type: "number", hint: "Check the Observatory for current Sun position" }
      ]
    },
    portal_identification: {
      name: "Portal Identification",
      description: "Identify which of the 12 Gates the Sun is currently passing through.",
      fields: [
        { key: "current_portal", label: "Current Portal (1-12)", type: "number", hint: "Based on Sun longitude: 0-30° = Portal 4, etc." }
      ]
    },
    moon_phase_test: {
      name: "Moon Phase Verification",
      description: "Identify the current moon phase using the Live Observatory.",
      fields: [
        { key: "moon_phase", label: "Current Moon Phase", type: "select", options: ["New Moon", "Waxing Crescent", "First Quarter", "Waxing Gibbous", "Full Moon", "Waning Gibbous", "Last Quarter", "Waning Crescent"] }
      ]
    },
    passover_calculation: {
      name: "Passover Date Calculation",
      description: "Calculate the Passover date for 2026 using the 364-Day Solar Calendar.",
      fields: [
        { key: "passover_date", label: "Passover 2026 Date (YYYY-MM-DD)", type: "text", hint: "Abib 14, 13 days after Abib 1 (April 18, 2026)" }
      ]
    },
    full_observatory_verification: {
      name: "Full Observatory Verification",
      description: "Comprehensive test: Verify Sun, Moon, Portal, and Biblical Day.",
      fields: [
        { key: "sun_longitude", label: "Sun Longitude (degrees)", type: "number" },
        { key: "moon_phase", label: "Moon Phase", type: "select", options: ["New Moon", "Waxing Crescent", "First Quarter", "Waxing Gibbous", "Full Moon", "Waning Gibbous", "Last Quarter", "Waning Crescent"] },
        { key: "current_portal", label: "Current Portal (1-12)", type: "number" },
        { key: "biblical_day", label: "Biblical Day Name", type: "select", options: ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Sabbath"] }
      ]
    },
    passover_verification_2026: {
      name: "Grade 12 Capstone: 2026 Passover Verification",
      description: "Complete verification of Passover 2026 using all calendar principles.",
      fields: [
        { key: "new_year_anchor", label: "New Year Anchor Date (YYYY-MM-DD)", type: "text", hint: "When is Abib 1, 2026?" },
        { key: "abib1_day", label: "Day of Week for Abib 1", type: "select", options: ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Sabbath"] },
        { key: "passover_date", label: "Passover Date (YYYY-MM-DD)", type: "text" },
        { key: "passover_day", label: "Day of Week for Passover", type: "select", options: ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Sabbath"] }
      ]
    }
  };

  // Get available tests based on grade
  const getAvailableTests = () => {
    const gradeNum = parseInt(grade) || 10;
    
    if (gradeNum === 10) {
      return ["sun_longitude_test", "portal_identification"];
    } else if (gradeNum === 11) {
      return ["moon_phase_test", "passover_calculation"];
    } else if (gradeNum === 12) {
      return ["full_observatory_verification", "passover_verification_2026"];
    }
    return ["sun_longitude_test"];
  };

  if (loading) {
    return (
      <div className="card" style={{ textAlign: "center", padding: "40px" }}>
        <div style={{ fontSize: "2rem", marginBottom: "15px" }}>🔭</div>
        <p style={{ color: "#888" }}>Loading Live Observatory...</p>
      </div>
    );
  }

  // Test selection view
  if (!testType) {
    const availableTests = getAvailableTests();
    
    return (
      <div data-testid="live-sky-test">
        <div className="card">
          <h2 style={{ color: "#FFD700", marginBottom: "10px", display: "flex", alignItems: "center", gap: "10px" }}>
            <span>🔭</span> Live Sky Test
          </h2>
          <p style={{ color: "#aaa", marginBottom: "20px" }}>
            Grade {grade || "10"} Observatory Verification • Required to Advance
          </p>

          {/* Test Status */}
          {testStatus && (
            <div style={{
              background: "#1a1a2a",
              padding: "15px",
              borderRadius: "8px",
              marginBottom: "20px"
            }}>
              <h3 style={{ color: "#87CEEB", margin: "0 0 10px 0", fontSize: "1rem" }}>
                📊 Your Test Status
              </h3>
              <div style={{ display: "flex", gap: "20px", flexWrap: "wrap" }}>
                <div>
                  <div style={{ color: "#FFD700", fontSize: "1.5rem", fontWeight: "bold" }}>
                    {testStatus.passed_tests?.length || 0}
                  </div>
                  <div style={{ color: "#888", fontSize: "0.8rem" }}>Tests Passed</div>
                </div>
                <div>
                  <div style={{ color: testStatus.all_requirements_met ? "#90EE90" : "#ff6b6b", fontSize: "1rem", fontWeight: "bold" }}>
                    {testStatus.all_requirements_met ? "✅ Complete" : "⏳ In Progress"}
                  </div>
                  <div style={{ color: "#888", fontSize: "0.8rem" }}>Status</div>
                </div>
              </div>
            </div>
          )}

          {/* Available Tests */}
          <h3 style={{ color: "#90EE90", marginBottom: "15px" }}>Available Tests</h3>
          <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            {availableTests.map(testId => {
              const test = tests[testId];
              const passed = testStatus?.passed_tests?.some(t => t.test_type === testId);
              
              return (
                <button
                  key={testId}
                  data-testid={`test-${testId}`}
                  onClick={() => setTestType(testId)}
                  style={{
                    background: passed ? "#1a3a1a" : "#1a1a2a",
                    border: passed ? "2px solid #90EE90" : "1px solid #333",
                    borderRadius: "12px",
                    padding: "20px",
                    textAlign: "left",
                    cursor: "pointer",
                    transition: "all 0.2s"
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div>
                      <div style={{ 
                        color: passed ? "#90EE90" : "#FFD700", 
                        fontWeight: "bold", 
                        marginBottom: "5px" 
                      }}>
                        {passed ? "✅" : "🔭"} {test.name}
                      </div>
                      <div style={{ color: "#888", fontSize: "0.85rem" }}>
                        {test.description}
                      </div>
                    </div>
                    <span style={{ color: "#555", fontSize: "1.5rem" }}>→</span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Current Observatory Data Reference */}
        <div className="card" style={{ marginTop: "15px" }}>
          <h3 style={{ color: "#87CEEB", marginBottom: "15px" }}>
            📡 Live Observatory Reference
          </h3>
          <p style={{ color: "#888", fontSize: "0.9rem", marginBottom: "15px" }}>
            Use this data to complete your tests. This is LIVE data from the Enochian Watchman.
          </p>
          
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "15px" }}>
            <div style={{ background: "#222", padding: "15px", borderRadius: "8px" }}>
              <div style={{ color: "#FFD700", fontWeight: "bold" }}>☀️ Sun</div>
              <div style={{ color: "#eee", marginTop: "5px" }}>
                Longitude: {celestialData?.sun?.longitude?.toFixed(1)}°
              </div>
            </div>
            <div style={{ background: "#222", padding: "15px", borderRadius: "8px" }}>
              <div style={{ color: "#C0C0C0", fontWeight: "bold" }}>🌙 Moon</div>
              <div style={{ color: "#eee", marginTop: "5px" }}>
                Phase: {celestialData?.moon?.phase_name}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Test taking view
  const currentTest = tests[testType];

  return (
    <div data-testid="live-sky-test-form">
      <div className="card">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
          <h2 style={{ color: "#FFD700", margin: 0 }}>
            🔭 {currentTest.name}
          </h2>
          <button
            onClick={() => { setTestType(null); setAnswers({}); setResult(null); }}
            style={{ background: "#333", padding: "8px 15px" }}
          >
            ← Back
          </button>
        </div>

        <p style={{ color: "#aaa", marginBottom: "25px" }}>
          {currentTest.description}
        </p>

        {/* Test Form */}
        <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
          {currentTest.fields.map(field => (
            <div key={field.key}>
              <label style={{ 
                display: "block", 
                color: "#FFD700", 
                marginBottom: "8px",
                fontWeight: "bold"
              }}>
                {field.label}
              </label>
              
              {field.type === "select" ? (
                <select
                  value={answers[field.key] || ""}
                  onChange={(e) => setAnswers({ ...answers, [field.key]: e.target.value })}
                  style={{
                    width: "100%",
                    padding: "12px",
                    background: "#222",
                    border: "1px solid #444",
                    borderRadius: "8px",
                    color: "#fff",
                    fontSize: "1rem"
                  }}
                >
                  <option value="">Select...</option>
                  {field.options.map(opt => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              ) : (
                <input
                  type={field.type}
                  value={answers[field.key] || ""}
                  onChange={(e) => setAnswers({ 
                    ...answers, 
                    [field.key]: field.type === "number" ? parseFloat(e.target.value) : e.target.value 
                  })}
                  placeholder={field.hint || ""}
                  style={{
                    width: "100%",
                    padding: "12px",
                    background: "#222",
                    border: "1px solid #444",
                    borderRadius: "8px",
                    color: "#fff",
                    fontSize: "1rem"
                  }}
                />
              )}
              
              {field.hint && (
                <p style={{ color: "#666", fontSize: "0.8rem", marginTop: "5px" }}>
                  💡 {field.hint}
                </p>
              )}
            </div>
          ))}
        </div>

        {/* Submit Button */}
        <button
          onClick={submitTest}
          disabled={submitting}
          data-testid="submit-test"
          style={{
            width: "100%",
            marginTop: "25px",
            background: submitting ? "#555" : "linear-gradient(135deg, #FFD700 0%, #FFA500 100%)",
            color: "#000",
            padding: "15px",
            fontWeight: "bold",
            fontSize: "1rem",
            borderRadius: "8px"
          }}
        >
          {submitting ? "Verifying..." : "Submit Test"}
        </button>

        {/* Result */}
        {result && (
          <div style={{
            marginTop: "20px",
            padding: "20px",
            background: result.passed ? "#1a3a1a" : "#3a1a1a",
            border: `2px solid ${result.passed ? "#90EE90" : "#ff6b6b"}`,
            borderRadius: "12px"
          }}>
            <h3 style={{ 
              color: result.passed ? "#90EE90" : "#ff6b6b", 
              margin: "0 0 10px 0" 
            }}>
              {result.passed ? "✅ TEST PASSED!" : "❌ Test Not Passed"}
            </h3>
            <div style={{ 
              color: "#FFD700", 
              fontSize: "1.5rem", 
              fontWeight: "bold",
              marginBottom: "15px"
            }}>
              Score: {result.score}%
            </div>
            
            {result.feedback?.map((fb, i) => (
              <p key={i} style={{ color: "#aaa", margin: "5px 0", fontSize: "0.9rem" }}>
                {fb}
              </p>
            ))}
            
            {result.passed && (
              <p style={{ color: "#90EE90", marginTop: "15px" }}>
                {result.message}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default LiveSkyTest;
